import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Card,
  Row,
  Col,
  Statistic,
  Tag,
  Button,
  Space,
  Select,
  DatePicker,
  Typography,
  Tooltip,
  Progress,
} from 'antd';
import {
  ReloadOutlined,
  DollarOutlined,
  BarChartOutlined,
  ClockCircleOutlined,
  CreditCardOutlined,
  LockOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { hasPermission } from '../../utils/permissions';
import {
  fetchAllPaymentsRequest,
  fetchPaymentStatisticsRequest,
} from '../../redux/actions/paymentActions';
import { getPaymentStatusTag } from './PaymentPanel';

const { Text, Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const fmtMoney = (val) =>
  val != null
    ? `₹ ${Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
    : '—';

const fmtDate = (val) => (val ? dayjs(val).format('DD MMM YYYY') : '—');

const getPaymentModeTag = (mode) => {
  const colors = { neft: 'blue', rtgs: 'geekblue', cheque: 'purple', cash: 'green', upi: 'cyan', dd: 'magenta' };
  return <Tag color={colors[mode] || 'default'}>{(mode || '').toUpperCase()}</Tag>;
};

const getStatusTag = (status) => {
  if (status === 'completed') return <Tag color="success">Completed</Tag>;
  if (status === 'pending')   return <Tag color="warning">Pending</Tag>;
  if (status === 'failed')    return <Tag color="error">Failed</Tag>;
  return <Tag>{status}</Tag>;
};

const Payment = () => {
  const dispatch = useDispatch();
  const permissions = useSelector((s) => s.auth.permissions || []);
  const { allPayments, statistics, loading } = useSelector((s) => s.payments);

  const canView = hasPermission(permissions, 'bill_receipt/view');

  const [dateRange, setDateRange] = useState(null);
  const [modeFilter, setModeFilter] = useState('');

  const loadData = useCallback(() => {
    const params = {};
    if (dateRange?.[0]) params.date_from = dateRange[0].format('YYYY-MM-DD');
    if (dateRange?.[1]) params.date_to   = dateRange[1].format('YYYY-MM-DD');
    if (modeFilter)     params.mode = modeFilter;

    dispatch(fetchAllPaymentsRequest(params));
    dispatch(fetchPaymentStatisticsRequest(params));
  }, [dispatch, dateRange, modeFilter]);

  useEffect(() => {
    if (canView) loadData();
  }, [canView]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!canView) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <div className="empty-state-icon"><LockOutlined /></div>
          <div className="empty-state-text">You don't have permission to view payments.</div>
        </div>
      </div>
    );
  }

  // Stats
  const totalPayments   = statistics?.total_payments    ?? 0;
  const totalPaid       = statistics?.total_amount_paid  ?? 0;
  const totalOutstanding= statistics?.total_outstanding  ?? 0;
  const byMode          = statistics?.by_mode            ?? [];

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: 'Bill #',
      key: 'bill',
      width: 130,
      render: (_, r) => (
        <Text style={{ color: '#1890ff', fontWeight: 600 }}>
          #{r.bill_receipt_id}
        </Text>
      ),
    },
    {
      title: 'PO #',
      dataIndex: 'purchase_order_id',
      key: 'po_id',
      width: 70,
      render: (id) => `#${id}`,
    },
    {
      title: 'Vendor',
      dataIndex: 'vendor_name',
      key: 'vendor_name',
      width: 160,
      ellipsis: true,
      render: (v) => <Tooltip title={v}><span>{v}</span></Tooltip>,
    },
    {
      title: 'Date',
      dataIndex: 'payment_date',
      key: 'payment_date',
      width: 110,
      render: fmtDate,
      sorter: (a, b) => new Date(a.payment_date) - new Date(b.payment_date),
    },
    {
      title: 'Mode',
      dataIndex: 'payment_mode',
      key: 'payment_mode',
      width: 90,
      render: getPaymentModeTag,
    },
    {
      title: 'Reference',
      dataIndex: 'reference_number',
      key: 'reference_number',
      width: 150,
      ellipsis: true,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 130,
      align: 'right',
      render: (v) => <Text strong style={{ color: '#1890ff' }}>{fmtMoney(v)}</Text>,
      sorter: (a, b) => a.amount - b.amount,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: getStatusTag,
    },
    {
      title: 'Paid By',
      dataIndex: 'paid_by_name',
      key: 'paid_by_name',
      width: 130,
      ellipsis: true,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      key: 'remarks',
      ellipsis: true,
      render: (v) => v ? (
        <Tooltip title={v}>
          <Text type="secondary" style={{ fontSize: 12 }}>{v}</Text>
        </Tooltip>
      ) : null,
    },
  ];

  return (
    <div className="page-container">
      <div className="page-content">
        {/* ── Page Header ──────────────────────────────────────────────────── */}
        <div className="page-header">
          <div className="page-header-title">
            <div className="page-header-icon"><DollarOutlined /></div>
            <div className="page-header-text">
              <h1>Payments</h1>
              <p>Track all vendor payments and outstanding balances</p>
            </div>
          </div>
        </div>

        {/* ── Statistics Cards ──────────────────────────────────────────────── */}
        <div className="stats-grid">
          <div className="stat-card" style={{ borderLeftColor: '#1890ff' }}>
            <div className="stat-icon" style={{ background: '#e6f4ff', color: '#1890ff' }}><CreditCardOutlined /></div>
            <div className="stat-content">
              <div className="stat-value" style={{ color: '#1890ff' }}>{totalPayments}</div>
              <div className="stat-label">Payments Recorded</div>
            </div>
          </div>
          <div className="stat-card" style={{ borderLeftColor: '#52c41a' }}>
            <div className="stat-icon" style={{ background: '#f6ffed', color: '#52c41a' }}><DollarOutlined /></div>
            <div className="stat-content">
              <div className="stat-value" style={{ color: '#52c41a' }}>
                ₹{Number(totalPaid).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </div>
              <div className="stat-label">Total Paid</div>
            </div>
          </div>
          <div className="stat-card" style={{ borderLeftColor: totalOutstanding > 0 ? '#fa8c16' : '#52c41a' }}>
            <div className="stat-icon" style={{ background: totalOutstanding > 0 ? '#fff7e6' : '#f6ffed', color: totalOutstanding > 0 ? '#fa8c16' : '#52c41a' }}><BarChartOutlined /></div>
            <div className="stat-content">
              <div className="stat-value" style={{ color: totalOutstanding > 0 ? '#fa8c16' : '#52c41a' }}>
                ₹{Number(totalOutstanding).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </div>
              <div className="stat-label">Outstanding</div>
            </div>
          </div>
        </div>

        {/* ── By Mode Breakdown — compact inline strip ──────────────────────── */}
        {byMode.length > 0 && (
          <div style={{ background: '#fff', borderRadius: 8, padding: '8px 14px', marginBottom: 12, boxShadow: '0 1px 4px rgba(0,0,0,.05)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#666' }}>By Mode:</span>
            {byMode.map((m) => (
              <span key={m.mode} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                {getPaymentModeTag(m.mode)}
                <span style={{ fontSize: 12, color: '#333', fontWeight: 500 }}>{fmtMoney(m.amount)} ({m.count})</span>
              </span>
            ))}
          </div>
        )}

        {/* ── Filters ───────────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
          <Title level={5} style={{ margin: 0 }}>All Payments</Title>
          <Space wrap>
            <RangePicker
              style={{ width: 260 }}
              format="DD MMM YYYY"
              value={dateRange}
              onChange={setDateRange}
              allowClear
              placeholder={['From Date', 'To Date']}
            />
            <Select
              placeholder="Filter by mode"
              style={{ width: 140 }}
              allowClear
              value={modeFilter || undefined}
              onChange={(v) => setModeFilter(v || '')}
            >
              {['neft', 'rtgs', 'cheque', 'cash', 'upi', 'dd'].map((m) => (
                <Option key={m} value={m}>{m.toUpperCase()}</Option>
              ))}
            </Select>
            <Button type="primary" onClick={loadData} icon={<ReloadOutlined />} loading={loading}>
              Apply
            </Button>
            <Button
              icon={<ClockCircleOutlined />}
              onClick={() => { setDateRange(null); setModeFilter(''); }}
            >
              Clear
            </Button>
          </Space>
        </div>

        {/* ── Table ─────────────────────────────────────────────────────────── */}
        <Table
          columns={columns}
          dataSource={allPayments}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: false, showTotal: (t, r) => `${r[0]}–${r[1]} of ${t}` }}
          scroll={{ x: 'max-content' }}
          size="small"
          locale={{
            emptyText: (
              <div className="empty-state" style={{ padding: 40 }}>
                <div className="empty-state-icon"><CreditCardOutlined /></div>
                <div className="empty-state-text">No payments found. Record the first payment from any verified bill.</div>
              </div>
            ),
          }}
        />
      </div>
    </div>
  );
};

export default Payment;
